import moment from "moment";
import { ImagesApi, QuickLinksApi } from "../services/index";
import LogrocketFuzzySanitizer from "logrocket-fuzzy-search-sanitizer";
import LogRocket from "logrocket";
import { version } from "../../package.json";
const defaultResultedOn = moment().format("YYYY-MM-DD");
export const sessionDetails = {
  namespaced: true,
  state: () => ({
    accessions: 0,
    accessionIds: [],
    specimens: 0,
    labelPrinterOverride: false,
    labelPrintAmount: 1,
    lastProvider: null,
    lastPathologist: null,
    lastPrefix: null,
    lastCytologyProtocolId: null,
    lastAccession: {},
    lastUsedSite: null,
    lastUsedProtocol: null,
    isRecordingLogRocket: false,
    apiUrl: process.env.VUE_APP_API_BASE_URL,
    imageFrame: {
      isOpen: false,
      urls: []
    },
    viewedSlideImages: [],
    activeSpellCheckers: [],
    scannedReqImages: [],
    scannedReqCounts: {
      cases: 0,
      images: 0
    },
    localImageUrls: [],
    dashboardFilters: {},
    lastReqScanId: null,
    pendingReqUploadId: null,
    cytologyPrompt: false,
    cytologyPrompted: false,
    resultedOnDate: defaultResultedOn,
    reScreen: false,
    logRocketIntervalId: null,
    altKeyDown: false
  }),

  mutations: {
    setLastUsedSite(state, payload) {
      state.lastUsedSite = payload;
    },
    setAccessionIds(state, payload) {
      state.accessionIds = payload;
    },
    setLastCytologyProtocol(state, payload) {
      state.lastCytologyProtocolId = payload;
    },
    toggleCytologyPrompt(state) {
      state.cytologyPrompt = !state.cytologyPrompt;
      if (state.cytologyPrompt) {
        state.cytologyPrompted = true;
      }
    },
    setRescreening(state, payload) {
      state.reScreen = payload;
    },
    setResultedOnDate(state, payload) {
      state.resultedOnDate = payload;
    },
    setCytologyPrompted(state, payload) {
      state.cytologyPrompted = payload;
    },

    setLastUsedProtocol(state, payload) {
      state.lastUsedProtocol = payload;
    },
    setLastPrefix(state, payload) {
      state.lastPrefix = payload;
    },
    setLastProvider(state, payload) {
      state.lastProvider = payload;
    },
    setLastPathologist(state, payload) {
      state.lastPathologist = payload;
    },
    setLastAccession(state, payload) {
      state.lastAccession = payload;
    },
    setViewedSlideImages(state, payload) {
      //! Set a max size of 25 ids to prevent memory leaks
      if (payload.length > 25) {
        payload = payload.slice(0, 25);
      }
      const payloadUniq = new Set(payload);
      state.viewedSlideImages = Array.from(payloadUniq);
    },
    setCurrentQuickLinks(state, payload) {
      state.quickLinks = payload;
    },
    setSessionDetails(state, labDetails = {}) {
      state.accessions = labDetails.accessions;
      state.specimens = labDetails.specimens;
      state.labelPrinterOverride = labDetails.labelPrinterOverride;
      state.labelPrintAmount = labDetails.labelPrintAmount;
      state.lastProvider = labDetails.lastProvider;
      state.lastPathologist = labDetails.lastPathologist;
      state.lastPrefix = labDetails.lastPrefix;
      state.lastAccession = labDetails.lastAccession;
    },
    clearSessionDetails(state) {
      const defaultState = {
        accessions: 0,
        accessionIds: [],
        specimens: 0,
        labelPrinterOverride: false,
        labelPrintAmount: 1,
        lastProvider: null,
        lastPathologist: null,
        lastPrefix: null,
        lastAccession: {},
        lastUsedSite: null,
        lastUsedProtocol: null,
        scannedReqImages: [],
        cytologyPrompted: false,
        lastReqScanId: null,
        reScreen: false,
        logRocketIntervalId: null
      };
      if (state.logRocketIntervalId) {
        clearInterval(state.logRocketIntervalId);
      }
      Object.keys(defaultState).forEach(key => {
        state[key] = defaultState[key];
      });
    },
    setLabelPrintOverride(state, payload) {
      state.labelPrinterOverride = payload;
    },
    setLabelPrintAmount(state, payload) {
      state.labelPrintAmount = payload;
    },
    setAccessions(state, payload) {
      state.accessions = payload;
    },
    setSpecimens(state, payload) {
      state.specimens = payload;
    },
    setIsRecordingLogRocket(state, payload) {
      state.isRecordingLogRocket = payload;
    },
    setImageFrameStatus(state, payload) {
      state.imageFrame.isOpen = payload;
    },
    setImageFrameData(state, payload) {
      state.imageFrame.urls = payload;
    },
    setActiveSpellCheckers(state, { name, isAdding }) {
      if (isAdding) {
        if (!state.activeSpellCheckers.includes(name)) {
          state.activeSpellCheckers.push(name);
        }
      } else {
        state.activeSpellCheckers = state.activeSpellCheckers.filter(e => e !== name);
      }
    },
    setScannedReqImages(state, payload) {
      state.scannedReqImages = payload;
    },
    setScannedReqCounts(state, payload) {
      state.scannedReqCounts = payload;
    },
    setLocalImageUrls(state, payload) {
      state.localImageUrls = payload;
    },
    setDashboardFilters(state, payload) {
      state.dashboardFilters = { ...state.dashboardFilters, ...payload };
    },
    setLastReqScanId(state, payload) {
      state.lastReqScanId = payload;
    },
    setPendingReqUploadId(state, payload) {
      state.pendingReqUploadId = payload;
    },
    setLogRocketIntervalId(state, payload) {
      state.logRocketIntervalId = payload;
    },
    setAltKeyDown(state, payload) {
      state.altKeyDown = payload;
    }
  },

  actions: {
    async useImageFrame({ commit }, payload) {
      commit("setImageFrameStatus", true);
      commit("setImageFrameData", []);
      let imageUrls = [];
      if (typeof payload === "string") {
        imageUrls = [{ imageSrc: payload, type: "png" }];
      } else {
        const imageFiles = payload.map(async imageId => {
          const fileBuffer = await ImagesApi.getImageContent(imageId);
          return {
            imageSrc: URL.createObjectURL(fileBuffer),
            type: fileBuffer.type
          };
        });
        imageUrls = await Promise.all(imageFiles);
      }
      if (imageUrls?.length) {
        commit("setImageFrameData", imageUrls);
      } else {
        window.alert("Error loading images.");
        commit("setImageFrameStatus", false);
      }
    },

    async accessionCase({ commit, state, rootState }, previousCase) {
      const { labPrefix, contacts, caseNumber, patientLastName, patientFirstName, caseId } =
        previousCase;
      const primaryProvider = contacts.find(e => e.isPrimary);

      if (labPrefix) {
        commit("setLastPrefix", null);
        const prefix = rootState.dropdowns.prefixes.find(prefix => prefix.id === labPrefix);
        if (prefix) {
          commit("setLastPrefix", prefix);
        }
      }
      if (state.lastAccession.caseId !== previousCase.caseId) {
        commit("setLastAccession", { caseNumber, patientLastName, patientFirstName, caseId });
        commit("setAccessions", state.accessions + 1);
      }
      commit("setAccessionIds", [...state.accessionIds.slice(0, 8), caseId]);
      commit("setLastProvider", primaryProvider);
    },
    async getRoleQuickLinks({ commit }) {
      const quickLinks = await QuickLinksApi.quickLinksByRole();
      commit("setCurrentQuickLinks", quickLinks);
    },
    async startRecordingLogRocket(
      { commit, state, rootState, dispatch },
      isAutoRecord,
      skipNotification
    ) {
      if (state.logRocketIntervalId) {
        clearInterval(state.logRocketIntervalId);
        commit("setLogRocketIntervalId", null);
      }
      commit("setIsRecordingLogRocket", true);
      const privateFieldNames = [
        "password",
        "patient",
        "patientSSN",
        "ssn",
        "dob",
        "patientDOB",
        "patientDob",
        "patientDateOfBirth",
        "dateOfBirth",
        "birth",
        "patientMRN",
        "mrn",
        "patientName",
        "name",
        "patientFirstName",
        "patientMiddleName",
        "patientLastName",
        "patientMaidenName",
        "accountNumber",
        "acctNumber",
        "patientAccountNumber",
        "refNumber",
        "alternateId",
        "address1",
        "address2",
        "addressLine1",
        "addressLine2",
        "line1",
        "line2",
        "address",
        "phone",
        "email",
        "groupNumber",
        "guarantor",
        "policyNumber",
        "comments",
        "patientFieldContains",
        "ipAddress",
        "pdf"
      ];
      const { requestSanitizer, responseSanitizer } = LogrocketFuzzySanitizer.setup([
        ...privateFieldNames
      ]);
      const logrocketApiKey = process.env.VUE_APP_LOGROCKET_API_KEY;
      try {
        LogRocket.init(logrocketApiKey, {
          release: version,
          network: {
            requestSanitizer,
            responseSanitizer
          }
        });
        LogRocket.identify(rootState.currentUser.id, {
          name: rootState.currentUser.displayName,
          email: rootState.currentUser.email
        });
        const sessionUrl = await new Promise(resolve => {
          LogRocket.getSessionURL(resolve);
        });
        if (!sessionUrl) {
          window.alert("An error occured in recording. Please try again.");
          return;
        }
        if (isAutoRecord) {
          if (!skipNotification) {
            window.notify("Your session is being recorded by LogRocket.", "success", 5000);
          }
          // Check every minute to see if LogRocket is still recording
          const intervalId = setInterval(async () => {
            await new Promise(resolve => {
              try {
                LogRocket.getSessionURL(resolve);
              } catch (error) {
                dispatch("sessionDetails/startRecordingLogRocket", true, true);
              }
            });
          }, 60000);
          commit("setLogRocketIntervalId", intervalId);
        }
        return sessionUrl;
      } catch (error) {
        window.notify("Error starting LogRocket recording.", "error", 5000);
      }
    },
    increaseSpecimenCount({ commit, state }, count) {
      const prevCount = state.specimens;
      commit("setSpecimens", prevCount + count);
    },
    addReqImageScans({ commit }, { formData, imageUrls, chunkIndex }) {
      return ImagesApi.uploadScans(formData).then(res => {
        if (res?.message && /chunk/i.test(res.message)) {
          console.log(res?.message + " " + chunkIndex);
        } else {
          window.notify(
            "Scan received. You will receive a notification when they are processed.",
            "warning"
          );
          commit("setLocalImageUrls", imageUrls);
        }
      });
    },
    clearLogRocketInterval({ commit, state }) {
      if (state.logRocketIntervalId) {
        clearInterval(state.logRocketIntervalId);
        commit("setLogRocketIntervalId", null);
      }
    }
  }
};
